import React, { Fragment, useEffect, useState } from 'react';
import { Row, Col, Button, Divider } from 'antd';
import {
  FileTextOutlined,
  FileSearchOutlined,
  HistoryOutlined,
  DownloadOutlined,
} from '@ant-design/icons';
import {
  CustomButton,
  CustomModal,
  CustomTable,
  CustomTabs,
  CustomText,
  CustomTextArea,
} from '../../../../components';
import { GoodTypes } from '../../types';
import GestorStatus from '../components/ByRoles/GestorStatus';
import GestorTableItems from '../components/ByRoles/GestorTableItems';

interface GoodsDetailViewProps {
  detailData?: any;
  setFooterLeft: (footer: React.ReactNode) => void;
  onVoucher?: () => void;
  onDocuments: (id: string) => void;
  onDownloadPDf: (id: string) => void;
  goodStates: any;
  goodPetitionItems?: any;
  formik: any;
  onSaveGestor?: any;
  updateItems?: boolean;
  setUpdateItems?: any;
  formikSolicitud?: any;
  setIsResponseOk?: any;
  isResponseOk?: any;
  downloadFile?: any;
  isApproveRequest?: boolean;
  isPendingFiles: boolean;
  isPendingPdf: boolean;
  isRequestedItems?: boolean;
}

const GoodsDetailView: React.FC<GoodsDetailViewProps> = ({
  detailData,
  setFooterLeft,
  onDocuments,
  onDownloadPDf,
  goodStates,
  goodPetitionItems,
  formik,
  formikSolicitud,
  isApproveRequest,
  isPendingFiles,
  isPendingPdf,
  isRequestedItems,
}) => {
  const [isRoleManage, setIsRoleManage] = useState(false);
  const [modalManageOne, setModalOne] = useState<boolean>(false);
  const [itemSelected, setItemSelected] = useState<any>();
  const [loadingOne, setLoadingOne] = useState(false);
  const [columTableBienes, setColumTableBienes] = useState<any>([]);
  const [columTableFijos, setColumTableFijos] = useState<any>([]);
  const [dataItems, setDataItems] = useState(detailData);

  // FOOTER
  React.useEffect(() => {
    setFooterLeft(
      <>
      <CustomButton
        label="Descargar documentos"
        disabled={(!detailData?.documents || detailData.documents.length === 0) || isPendingFiles}
        onClick={() => {
          onDocuments(detailData?.id || '');
        }}
        icon={<DownloadOutlined />}
        size="middle"
        buttonType="secondary"
      />

      <CustomButton
        style={{ marginLeft: '20px' }}
        label="Generar comprobante"
        disabled={isPendingPdf || detailData?.status_id !== 7}
        onClick={() => {
          onDownloadPDf(detailData?.id_petition || '');
        }}
        icon={<DownloadOutlined />}
        size="middle"
        buttonType="secondary"
      />
      </>
    );
  }, [detailData, dataItems, setFooterLeft, isPendingFiles, isPendingPdf]);

  useEffect(() => {
    const canManage = !!detailData?.can_manage_user;
    const hasProductCode = !!(isRequestedItems && detailData.items[0]?.plate_code && detailData.goods_type === GoodTypes.ACTIVO_FIJO);
    const validate = canManage || hasProductCode;
    const isRoleValidate = ([12, 14, 7] as number[]).includes(detailData?.status_id);

    setIsRoleManage(detailData?.can_manage && isApproveRequest);
    setColumTableBienes([
      {
        title: 'Item',
        dataIndex: 'index',
        key: 'index',
        align: 'center',
        render: (_: any, __: any, index: number) => index + 1,
      },
      {
        title: 'Código de producto',
        dataIndex: 'product_code',
        key: 'product_code',
        align: 'center',
        render: (data: any) => data ? data : "-"
      },
      {
        title: 'Descripción',
        dataIndex: 'description',
        key: 'description',
        align: 'center',
        render: (data: any) => data ? data : "-"
      },
      {
        title: 'Grupo de bienes',
        dataIndex: 'good_class',
        key: 'good_class',
        align: 'center',
        render: (data: any) => data ? data.group_code + " - " + data.name : "-"
      },
      {
        title: 'Unidad de medida',
        dataIndex: 'unit_measurement',
        key: 'unit_measurement',
        align: 'center',
        render: (data: any) => data ? data.name : "-"
      },
      {
        title: 'Cantidad solicitada',
        dataIndex: 'quantity_requested',
        key: 'quantity_requested',
        align: 'center',
        render: (data: any) => data ? data : "-"
      },
      {
        title: 'Cantidad entregada',
        dataIndex: 'quantity_received',
        key: 'quantity_received',
        align: 'center',
        render: (data: any) => data ? data : "-"
      },
      {
        title: 'Estado por elemento',
        dataIndex: 'status',
        key: 'status',
      },
      {
        title: 'Acciones',
        key: 'actions',
        hidden: isRequestedItems || !validate,
        render: (item: any, record: any) => (
          <>
            {record?.status !== 'No entregado' &&
              <CustomButton
                label="Gestionar"
                onClick={() => handleModalOneItemOpen(item)}
                size="middle"
              />
            }
          </>
        ),
      },
    ],)
    setColumTableFijos([
      {
        title: 'Item',
        dataIndex: 'index',
        key: 'index',
        render: (_: any, __: any, index: number) => index + 1,
      },
      {
        title: 'Código / placa',
        dataIndex: 'article',
        hidden: (!validate && !isRoleValidate),
        key: 'article',
        render: (_: any, record: any) => record.plate_code,
      },
      {
        title: 'Descripción',
        dataIndex: 'description',
        hidden: (!validate && !isRoleValidate),
        key: 'description',
      },
      {
        title: 'Costo histórico',
        dataIndex: 'historical_cost',
        hidden: (!validate && !isRoleValidate),
        key: 'historical_cost',
        render: (value: number) => 
          value !== null && value !== undefined
            ? new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP' }).format(value)
            : '-',
      },
      {
        title: 'Nueva ubicación',
        dataIndex: 'location',
        hidden: (!validate && !isRoleValidate),
        key: 'location',
      },
      {
        title: 'Artículo',
        dataIndex: 'article',
        key: 'article',
        hidden: validate,
        render: (_: any, record: any) => record.item?.code + ' - ' + record.item?.name,
      },
      {
        title: 'Clase de activos',
        dataIndex: 'asset_class',
        key: 'aset_class',
        hidden: validate,
        render: (_: any, record: any) => record.item?.asset_class?.code + ' - ' + record.item?.asset_class?.name,
      },
      {
        title: 'Estado por elemento',
        dataIndex: 'status',
        key: 'status',
      },
      {
        title: 'Acciones',
        key: 'actions',
        hidden: isRequestedItems || !validate,
        render: (item: any, record: any) => (
          <>
            {record?.status !== 'No entregado' &&
              <CustomButton
                label="Gestionar"
                onClick={() => handleModalOneItemOpen(item)}
                size="middle"
              />
            }
          </>
        ),
      },
    ],)
  }, [detailData]);
  const documentColumns = [
    {
      title: 'Item',
      dataIndex: 'index',
      key: 'index',
      render: (_: any, __: any, index: number) => index + 1,
    },
    {
      title: 'Nombre',
      dataIndex: 'name',
      key: 'name',
    },
  ];

  // Modal de gestionar un item
  const handleModalOneItemOpen = (item: any) => {
    setModalOne(true);
    setItemSelected(item);
    formikSolicitud.setFieldValue('status_id', [15,16].includes(item.status_id) ? item.status_id : null);
  };

  const handleModalOneItemCancel = () => {
    setModalOne(false);
    setLoadingOne(false)
    setItemSelected([]);
  };

  const handleOneItemSave = async () => {
    setLoadingOne(true);
    
    if (Array.isArray(itemSelected)) {
      await setDataItems({
        ...dataItems,
        items: dataItems?.items.map((it: any) =>
          itemSelected.includes(it.id)
            ? {
                ...it,
                status_id: formikSolicitud.values.status_id,
                status: goodPetitionItems.find(
                  (petition: any) => petition.id === formikSolicitud.values.status_id
                )?.name,
              }
            : it
        ),
      });
    } else {
      await setDataItems({
        ...dataItems,
        items: dataItems?.items.map((it: any) =>
          it.id === itemSelected.id 
            ? {
                ...it,
                status_id: formikSolicitud.values.status_id,
                status: goodPetitionItems.find(
                  (petition: any) => petition.id === formikSolicitud.values.status_id
                )?.name,
              }
            : it
        ),
      });
    }
    setLoadingOne(false);
    handleModalOneItemCancel();
    setItemSelected([]);
  };

  useEffect(() => {
    if (!isApproveRequest) {
      formik.setFieldValue('items', dataItems?.items);
    }
  }, [dataItems]);

  return (
    <Fragment>
      {isRoleManage && (
        <GestorStatus
          label_status="Estado de la solicitud"
          goodStates={
            goodStates
              ?.filter((state: any) => state.id && [9, 10].includes(state.id))
          }
          detailData={dataItems}
          formikSolicitud={formikSolicitud}
        />
      )}

    {(isRoleManage || detailData?.status?.id === 72) && !isRequestedItems &&(<Row gutter={[16, 16]}>
        <Col xs={24} sm={24} md={24} lg={24}>
          <CustomTextArea
            label="Observaciones"
            name="reason"
            placeholder="Seleccione"
            value={formik.values.reason}
            error={formik.errors.reason}
            touched={formik.touched.reason}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          />
        </Col>
      </Row>
      )}

      <CustomTabs
        defaultActiveKey="1"
        items={[
          {
            key: '1',
            label: (
              <Button type="link" icon={<FileTextOutlined />}>
                Información general
              </Button>
            ),
            children: (
              <>
                <Row gutter={[16, 16]} style={{ marginBottom: '20px' }}>
                  <Col span={8}>
                    <div className="mb-4 mt-8">
                      <CustomText text="Solicitante:" strong fontSize="small" />
                      <CustomText
                        text={detailData?.document_requester + ' - ' + detailData?.requester}
                        fontSize="middle"
                      />
                    </div>
                  </Col>
                  <Col span={8}>
                    <div className="mb-4 mt-8">
                      <CustomText text="Fecha de solicitud:" strong fontSize="small" />
                      <CustomText
                        text={detailData?.query_date}
                        fontSize="middle"
                      />
                    </div>
                  </Col>
                  <Col span={8}>
                    <div className="mb-4 mt-8">
                      <CustomText text="Dependencia:" strong fontSize="small" />
                      <CustomText
                        text={detailData?.dependency}
                        fontSize="middle"
                      />
                    </div>
                  </Col>
                  <Col span={8}>
                    <div className="mb-4 mt-8">
                      <CustomText text="Ubicación:" strong fontSize="small" />
                      <CustomText
                        text={detailData?.location}
                        fontSize="middle"
                      />
                    </div>
                  </Col>
                  <Col span={8}>
                    <div className="mb-4 mt-8">
                      <CustomText
                        text="Tipo de solicitud:"
                        strong
                        fontSize="small"
                      />
                      <CustomText
                        text={detailData?.goods_type}
                        fontSize="middle"
                      />
                    </div>
                  </Col>

                  <Col span={8}>
                    <div className="mb-4 mt-8">
                      <CustomText
                        text="Estado de solicitud:"
                        strong
                        fontSize="small"
                      />
                      <CustomText text={detailData?.status?.status} fontSize="middle" />
                    </div>
                  </Col>
                  <Col span={8}>
                    <div className="mb-4 mt-8">
                      <CustomText
                        text="Jefe de depedencia:"
                        strong
                        fontSize="small"
                      />
                      <CustomText
                        text={detailData?.approved_by ? detailData?.approved_by : 'Sin registrar'}
                        fontSize="middle"
                      />
                    </div>
                  </Col>
                  <Col span={8}>
                    <div className="mb-4 mt-8">
                      <CustomText
                        text="Observaciones:"
                        strong
                        fontSize="small"
                      />
                      <CustomText
                        text={detailData?.observations ? detailData?.observations : 'Sin registrar'}
                        fontSize="middle"
                      />
                    </div>
                  </Col>
                </Row>

                <GestorTableItems
                  detailData={isApproveRequest ? detailData : dataItems}
                  setModalManageAll={setModalOne}
                  columnsFijos={columTableFijos}
                  columnsBienes={columTableBienes}
                  isRoleManage={detailData?.can_manage_user && !isRequestedItems}
                  isRequestedItems={isRequestedItems}
                  onSelectedRowsChange={(selectedRowKeys: any) =>
                    setItemSelected(selectedRowKeys)
                  }
                />
              </>
            ),
          },
          {
            key: '2',
            label: (
              <Button type="link" icon={<FileSearchOutlined />}>
                Documentos
              </Button>
            ),
            children: (
              <>
                <CustomTable
                  dataSource={detailData?.documents?.map(
                    (doc: any, index: number) => ({
                      key: index,
                      createdAt: doc.created_at,
                      name: doc.url?.split('/').pop(),
                      link: doc.url,
                    })
                  )}
                  columns={documentColumns}
                  pagination={false}
                />
              </>
            ),
          },
          {
            key: '3',
            label: (
              <Button type="link" icon={<HistoryOutlined />}>
                Historial de la solicitud
              </Button>
            ),
            children: (
              <CustomTable
                columns={[
                  {
                    title: 'Item',
                    dataIndex: 'index',
                    key: 'index',
                    render: (_: any, __: any, index: number) => index + 1,
                  },
                  {
                    title: 'Fecha',
                    dataIndex: 'date',
                    key: 'date',
                  },
                  {
                    title: 'Usuario',
                    dataIndex: 'user',
                    key: 'user',
                    render: (_: any, record: any) => record.document +  ' - ' + record.user,
                  },
                  {
                    title: 'Observaciones',
                    dataIndex: 'observations',
                    key: 'observations',
                  },
                  {
                    title: 'Estado',
                    dataIndex: 'comment',
                    key: 'comment',
                    render: (text: string) => {
                      if (!text) return '';
                      return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
                    },
                  },
                ]}
                dataSource={
                  detailData?.history?.map((item: any, index: number) => ({
                    key: index,
                    date: item.date,
                    comment: item.comment,
                    user: item.user,
                    document: item.document,
                    observations: item.observations,
                  })) || []
                }
                pagination={false}
              />
            ),
          },
        ]}
      />
      <Divider />

      <CustomModal
        title={''}
        labelSubmit="Aceptar"
        open={modalManageOne}
        width={1000}
        onCancel={handleModalOneItemCancel}
        onSave={handleOneItemSave}
        disabledBtn={
          formik.values.status_element_id 
        }
        isLoadingSave={loadingOne}
      >
        <GestorStatus
          label_status={`¿Desea gestionar el estado del elemento ${detailData?.goods_type == GoodTypes.ACTIVO_FIJO ? 'de activos' : detailData?.goods_type == GoodTypes.BIEN_CONSUMO ? 'de bienes' : ''}?`}
          goodStates={
            goodStates
              ?.filter((state: any) => state.id && [15, 16].includes(state.id))
          }
          detailData={dataItems}
          formikSolicitud={formikSolicitud}
        />
      </CustomModal>
    </Fragment>
  );
};

export default GoodsDetailView;
